// ============================================
// CARRITOFLOTANTE.JS — Botón pill + panel lateral del carrito
// + hook useCarrito() para agregar items desde tarjetas
// ============================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authHeaders } from '../utils/storage';
import {
  ShoppingCart, Package, Lock, Trash2, Link2, ArrowRight, ArrowLeft, X, Minus, Plus,
} from 'lucide-react';
import { tokens } from '../styles/tokens';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloBotonPrimario, estiloBotonSecundario,
} from './helpersClientes';
import { useClienteTienda } from './SelectorCliente';
import { useUsuario } from '../context/UsuarioContext';
import { useMoneda } from './SelectorMoneda';
import { useIvaModo, conIvaSegun } from './SelectorIva';
import ModalCompartir from './ModalCompartir';
import { useResponsive } from '../hooks/useResponsive';

const EVT = 'beep-carrito-changed';
const EVT_OPEN = 'beep-carrito-open';

function vendedorIdDe(usuario) {
  return usuario?.correo || usuario?.email || usuario?.nombre || 'sin-vendedor';
}

export function notificarCarritoCambio() {
  window.dispatchEvent(new CustomEvent(EVT));
}

// Abre el panel del carrito desde cualquier componente que tenga
// montado <CarritoFlotante />. Útil para botones de carrito que viven
// fuera del propio CarritoFlotante (ej. header de PaginaProducto).
export function abrirCarrito() {
  window.dispatchEvent(new CustomEvent(EVT_OPEN));
}

// CO-2: navega al previsualizador de cotizacion pasando el carrito_id.
// Dashboard escucha este evento y hace setActivo('cotizaciones.previsualizador', carritoId).
export function irACotizacion(carritoId) {
  window.dispatchEvent(new CustomEvent('beep-ir-cotizacion', { detail: { carritoId } }));
}

// ROUND-TRIP: vuelve de La Tienda al editor de la cotizacion. Limpia la marca
// "agregando" del carrito (cotizacion_destino_id = null) y dispara el evento que
// Dashboard escucha para abrir el editor en modo "regreso" (cabecera + lineas del carrito).
export async function volverACotizacion(carritoId, cotizacionId) {
  try {
    if (carritoId != null) {
      await fetch(`${API_BASE_CLI}/api/tienda/carritos/${carritoId}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cotizacion_destino_id: null }),
      });
    }
  } catch (e) {
    // no critico: aunque falle limpiar la marca, igual regresamos al editor
  }
  window.dispatchEvent(new CustomEvent('beep-volver-cotizacion', { detail: { cotizacionId, carritoId } }));
}

// Hook: obtiene/crea el carrito activo del cliente actual y expone agregarItem.
export function useCarrito() {
  const { usuario } = useUsuario();
  const [cliente] = useClienteTienda();
  const [carrito, setCarrito] = useState(null);
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);

  const vendedorId = vendedorIdDe(usuario);

  const obtenerOCrear = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/tienda/carritos?vendedor_id=${encodeURIComponent(vendedorId)}`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendedor_id: vendedorId,
          cliente_id: cliente?.id || null,
          cliente_nombre: cliente?.nombre || null,
        }),
      }).then(x => x.json());
      if (r.ok) setCarrito(r.carrito);
      return r.carrito;
    } finally { setCargando(false); }
  }, [vendedorId, cliente]);

  const recargar = useCallback(async (carritoId) => {
    const id = carritoId || carrito?.id;
    if (!id) return;
    // SEGURIDAD-ENDPOINTS-ABIERTOS (2026-09-18): el carrito ya exige sesion y comprueba que sea
    // tuyo (antes, con el id a mano, se veia el de cualquier vendedor con su subtotal).
    const r = await fetch(`${API_BASE_CLI}/api/tienda/carritos/${id}`, { headers: authHeaders() }).then(x => x.json());
    if (r.ok) {
      setCarrito(r.carrito);
      setItems(r.items || []);
    }
  }, [carrito]);

  // ---- Flujo optimista de cantidad (CARRITO-LAG) -------------------------
  // Refs para evitar leer estado viejo en clics rapidos y para el debounce
  // del PATCH por item (un solo guardado con la cantidad final, sin GET por clic).
  const itemsRef = useRef(items);                 // copia sincrona de items
  const timersRef = useRef({});                   // timer de debounce por item_id
  const cantidadPendienteRef = useRef({});        // cantidad final pendiente por item_id
  const carritoIdRef = useRef(null);              // id del carrito actual
  const recargarRef = useRef(recargar);           // ultima version de recargar

  useEffect(() => { itemsRef.current = items; }, [items]);
  useEffect(() => { carritoIdRef.current = carrito?.id || null; }, [carrito]);
  useEffect(() => { recargarRef.current = recargar; }, [recargar]);

  // Envia el PATCH con la cantidad final pendiente del item. Si falla,
  // resincroniza con un GET (el optimismo local pudo quedar desfasado).
  const enviarPatch = useCallback(async (itemId) => {
    const carritoId = carritoIdRef.current;
    const cantidad = cantidadPendienteRef.current[itemId];
    delete cantidadPendienteRef.current[itemId];
    if (!carritoId || cantidad == null) return;
    try {
      const r = await fetch(`${API_BASE_CLI}/api/tienda/carritos/${carritoId}/items/${itemId}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cantidad }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
    } catch (e) {
      console.error('[carrito cambiarCantidad]', e);
      if (recargarRef.current) recargarRef.current(carritoId);
      window.alert('No se pudo actualizar la cantidad. Sincronizando de nuevo.');
    }
  }, []);

  // Manda de inmediato cualquier PATCH pendiente (al cerrar/desmontar) para
  // no perder el ultimo cambio que aun estaba en debounce.
  const flushPendientes = useCallback(() => {
    Object.keys(timersRef.current).forEach((itemId) => {
      if (timersRef.current[itemId]) {
        clearTimeout(timersRef.current[itemId]);
        timersRef.current[itemId] = null;
        enviarPatch(itemId);
      }
    });
  }, [enviarPatch]);

  useEffect(() => {
    let activo = true;
    obtenerOCrear().then(c => { if (activo && c) recargar(c.id); });
    return () => { activo = false; };
    // eslint-disable-next-line
  }, [vendedorId, cliente?.id]);

  useEffect(() => {
    const onCambio = () => { if (carrito?.id) recargar(carrito.id); };
    window.addEventListener(EVT, onCambio);
    return () => window.removeEventListener(EVT, onCambio);
  }, [carrito, recargar]);

  // agregarItem(producto, cantidad, extra?). `extra` son campos OPCIONALES del body
  // que se mandan tal cual (ej. precio_forzado_mxn + snapshot para items congelados de
  // una coleccion). La Tienda llama sin `extra` -> body identico al de siempre.
  const agregarItem = useCallback(async (producto, cantidad = 1, extra = {}) => {
    let car = carrito;
    if (!car) car = await obtenerOCrear();
    if (!car) throw new Error('No se pudo crear carrito');
    const r = await fetch(`${API_BASE_CLI}/api/tienda/carritos/${car.id}/items?vendedor_id=${encodeURIComponent(vendedorId)}`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tipo: producto.tipo,
        referencia_id: producto.referencia_id,
        cantidad,
        ...(extra && typeof extra === 'object' ? extra : {}),
      }),
    }).then(x => x.json());
    if (!r.ok) throw new Error(r.error || 'error');
    notificarCarritoCambio();
    return r;
  }, [carrito, obtenerOCrear, vendedorId]);

  // Aplica una nueva cantidad ENTERA (minimo 1) a un item: optimista local
  // (cantidad + subtotal + totales) al instante y PATCH con debounce ~450 ms,
  // sin GET por clic. Reusada por cambiarCantidad (delta) y fijarCantidad (valor).
  const aplicarNuevaCantidad = useCallback((itemId, calcular) => {
    const prev = itemsRef.current;
    let nuevaCantidad = null;
    const next = prev.map((it) => {
      if (it.id !== itemId) return it;
      // Enteros, minimo 1. Math.round corrige errores de flotante y decimales heredados.
      const cant = Math.max(1, Math.round(calcular(Number(it.cantidad) || 0)));
      nuevaCantidad = cant;
      const precio = Number(it.precio_unitario_mxn) || 0;
      return { ...it, cantidad: cant, subtotal_mxn: Math.round(precio * cant * 100) / 100 };
    });
    if (nuevaCantidad == null) return; // item no encontrado
    itemsRef.current = next;           // sincrono: clics rapidos acumulan bien
    setItems(next);
    // Recalcular totales del carrito desde los items locales (subtotal = SUM(subtotal),
    // total_items = SUM(cantidad)), para que el subtotal y el pill cambien al instante.
    const subtotal = next.reduce((s, it) => s + (Number(it.subtotal_mxn) || 0), 0);
    const totalItems = next.reduce((s, it) => s + (Number(it.cantidad) || 0), 0);
    setCarrito((c) => (c ? { ...c, subtotal_mxn: subtotal, total_items: totalItems } : c));
    // Debounce del PATCH por item: solo se guarda el valor final tras el ultimo cambio.
    cantidadPendienteRef.current[itemId] = nuevaCantidad;
    if (timersRef.current[itemId]) clearTimeout(timersRef.current[itemId]);
    timersRef.current[itemId] = setTimeout(() => {
      timersRef.current[itemId] = null;
      enviarPatch(itemId);
    }, 450);
  }, [enviarPatch]);

  // Botones -/+ : suma/resta un entero (minimo 1).
  const cambiarCantidad = useCallback((itemId, delta) => {
    aplicarNuevaCantidad(itemId, (actual) => actual + delta);
  }, [aplicarNuevaCantidad]);

  // Input editable: fija la cantidad absoluta escrita (entero, minimo 1).
  const fijarCantidad = useCallback((itemId, valor) => {
    aplicarNuevaCantidad(itemId, () => Number(valor) || 0);
  }, [aplicarNuevaCantidad]);

  const quitarItem = useCallback(async (itemId) => {
    if (!carrito) return;
    const r = await fetch(`${API_BASE_CLI}/api/tienda/carritos/${carrito.id}/items/${itemId}`, {
      method: 'DELETE',
      headers: { ...authHeaders() },
    }).then(x => x.json());
    if (r.ok) notificarCarritoCambio();
    return r;
  }, [carrito]);

  const vaciar = useCallback(async () => {
    if (!carrito) return;
    const r = await fetch(`${API_BASE_CLI}/api/tienda/carritos/${carrito.id}/vaciar`, {
      method: 'POST',
      headers: { ...authHeaders() },
    }).then(x => x.json());
    if (r.ok) notificarCarritoCambio();
    return r;
  }, [carrito]);

  // CO-3: vincular/desvincular expediente al carrito (PUT al backend).
  // oportunidadId puede ser numero (vincular) o null (desvincular).
  const vincularOportunidad = useCallback(async (oportunidadId) => {
    if (!carrito?.id) return;
    const r = await fetch(`${API_BASE_CLI}/api/tienda/carritos/${carrito.id}`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ oportunidad_id: oportunidadId }),
    }).then(x => x.json());
    if (r.ok) {
      // Optimismo: actualizar local sin esperar otro fetch.
      setCarrito(r.carrito);
      notificarCarritoCambio();
    }
    return r;
  }, [carrito]);

  return {
    carrito, items, cargando,
    agregarItem, cambiarCantidad, fijarCantidad, quitarItem, vaciar, vincularOportunidad,
    flushPendientes,
    recargar: () => recargar(),
  };
}

// ============================================
// Componente flotante: pill arriba derecha + panel slide
// ============================================

export default function CarritoFlotante({ triggerOculto = false } = {}) {
  const { carrito, items, cambiarCantidad, fijarCantidad, quitarItem, vaciar, flushPendientes } = useCarrito();
  const [abierto, setAbierto] = useState(false);
  const [modalCompartir, setModalCompartir] = useState(false);
  const { formatear } = useMoneda();
  // Switch compartido Con IVA / Sin IVA (solo afecta lo MOSTRADO en el carrito).
  const [ivaModo] = useIvaModo();
  const { isMobile } = useResponsive();

  // Cierra el panel mandando antes cualquier cambio de cantidad pendiente
  // (que aun estaba en debounce) para no perderlo.
  const cerrar = useCallback(() => {
    flushPendientes();
    setAbierto(false);
  }, [flushPendientes]);

  // CO-2: "Generar cotizacion" ya NO crea ni exige expediente aqui. Guarda los
  // cambios de cantidad pendientes (flush) y navega al previsualizador, donde se
  // vincula cliente/expediente y se guarda. Requisito unico: que haya items.
  const generarCotizacion = useCallback(() => {
    flushPendientes();
    if (!carrito?.id || items.length === 0) return;
    irACotizacion(carrito.id);
    setAbierto(false);
  }, [carrito, items.length, flushPendientes]);

  // ROUND-TRIP: si el carrito esta "agregando a" una cotizacion, el boton principal
  // del panel cambia de "Generar cotizacion" a "Volver a la cotizacion".
  const agregandoA = carrito?.cotizacion_destino_id
    ? { id: carrito.cotizacion_destino_id, etiqueta: carrito.cotizacion_destino_folio || carrito.cotizacion_destino_nombre || 'la cotizacion' }
    : null;
  const volverDesdeCarrito = useCallback(() => {
    flushPendientes();
    if (!carrito?.id) return;
    volverACotizacion(carrito.id, carrito.cotizacion_destino_id);
    setAbierto(false);
  }, [carrito, flushPendientes]);

  // Apertura externa: cualquier botón en otra parte de la app puede
  // dispararla con abrirCarrito().
  useEffect(() => {
    const onOpen = () => setAbierto(true);
    window.addEventListener(EVT_OPEN, onOpen);
    return () => window.removeEventListener(EVT_OPEN, onOpen);
  }, []);

  // Flush al desmontar: guarda el ultimo cambio pendiente.
  useEffect(() => () => { flushPendientes(); }, [flushPendientes]);

  const totalItems = Number(carrito?.total_items || 0);
  const subtotal = Number(carrito?.subtotal_mxn || 0);

  // Body lock cuando carrito abierto en mobile
  useEffect(() => {
    if (!abierto || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [abierto, isMobile]);

  // Estilos del botón trigger según device
  const triggerStyle = isMobile
    ? {
        position: 'fixed', bottom: 16, right: 16, zIndex: 1100,
        padding: '12px 18px', borderRadius: 99,
        background: COLOR_OSCURO, color: '#fff', fontFamily: PP,
        border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
        boxShadow: '0 8px 20px rgba(24,95,165,0.4)',
        display: 'flex', alignItems: 'center', gap: 8,
        minHeight: 52,
        touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
      }
    : {
        position: 'fixed', top: 14, right: 14, zIndex: 1100,
        padding: '8px 16px', borderRadius: 99,
        background: COLOR_OSCURO, color: '#fff', fontFamily: PP,
        border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
        boxShadow: '0 4px 12px rgba(24,95,165,0.35)',
        display: 'flex', alignItems: 'center', gap: 8,
      };

  // Estilos del panel según device
  const panelStyleMobile = {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200,
    background: '#fff',
    borderRadius: '20px 20px 0 0',
    maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    fontFamily: PP, color: COLOR_GRIS,
    boxShadow: '0 -8px 24px rgba(0,0,0,0.15)',
    transform: 'translateY(0)',
    // UI-1.E: slide-in desde abajo al montar (antes la transicion no disparaba porque el panel nace ya abierto)
    animation: 'cartSlideUp 0.28s cubic-bezier(0.32, 0.72, 0, 1)',
  };
  const panelStyleDesktop = {
    position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(100%, 400px)',
    background: '#fff', zIndex: 1200, boxShadow: '-8px 0 24px rgba(0,0,0,0.15)',
    display: 'flex', flexDirection: 'column', fontFamily: PP, color: COLOR_GRIS,
    // UI-1.E: slide-in desde la derecha al montar
    animation: 'cartSlideRight 0.22s cubic-bezier(0.32, 0.72, 0, 1)',
  };

  return (
    <>
      {!triggerOculto && (
        <button
          onClick={() => setAbierto(true)}
          style={triggerStyle}
        >
          <ShoppingCart size={14} strokeWidth={2} />
          <span>{totalItems > 0 ? `${totalItems} item${totalItems === 1 ? '' : 's'}` : 'Carrito'} · {formatear(conIvaSegun(ivaModo, subtotal), 'MXN')}</span>
        </button>
      )}

      {/* Backdrop siempre presente (mobile y desktop) cuando abierto */}
      {(abierto || isMobile) && (
        <div
          onClick={cerrar}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1199,
            opacity: abierto ? 1 : 0,
            pointerEvents: abierto ? 'auto' : 'none',
            transition: 'opacity 0.25s',
          }}
        />
      )}

      {abierto && (
        <>
          <div style={isMobile ? panelStyleMobile : panelStyleDesktop}>
            {isMobile && (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
                <div style={{ width: 40, height: 4, background: '#d1d5db', borderRadius: 2 }} />
              </div>
            )}
            <div style={{ padding: '14px 20px 12px', borderBottom: `1px solid ${COLOR_BORDE}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 700, color: COLOR_OSCURO }}>
                <ShoppingCart size={18} strokeWidth={2} /> Mi carrito {totalItems > 0 && <span style={{ color: COLOR_GRIS_CLARO, fontWeight: 500 }}>({totalItems})</span>}
              </div>
              <button
                onClick={cerrar}
                aria-label="Cerrar"
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: COLOR_GRIS_CLARO, padding: '4px 8px',
                  minWidth: 44, minHeight: 44,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
                }}
              ><X size={24} strokeWidth={2} /></button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
              {items.length === 0 && (
                <div style={{ color: COLOR_GRIS_CLARO, textAlign: 'center', padding: 30, fontSize: 13 }}>
                  Aún no has agregado items al carrito.
                </div>
              )}
              {items.map(it => {
                return (
                <div key={it.id} style={{
                  padding: 10, marginBottom: 10, border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
                }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <div className="carrito-thumb" style={{
                      width: 60, height: 60, flexShrink: 0,
                      background: COLOR_FONDO, borderRadius: 6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden', fontSize: 26, color: COLOR_GRIS_CLARO,
                    }}>
                      {it.imagen_url ? (
                        <img
                          src={it.imagen_url} alt="" loading="lazy"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : <Package size={26} strokeWidth={1.5} aria-label="Sin imagen" />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: COLOR_OSCURO,
                        lineHeight: 1.3,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>{it.nombre}</div>
                      {it.modelo && (
                        <div style={{
                          fontSize: 12, color: '#6d28d9', fontWeight: 800, fontFamily: 'monospace', marginTop: 2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{it.modelo}</div>
                      )}
                      {it.marca && (
                        <div style={{
                          fontSize: 10, color: COLOR_GRIS_CLARO, marginTop: 1,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{it.marca}</div>
                      )}
                      {it.es_pactado && (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '1px 6px', borderRadius: 4,
                          background: tokens.warningSoft, color: tokens.warning, fontSize: 10, fontWeight: 700,
                          marginTop: 4,
                        }}><Lock size={11} strokeWidth={2} /> PACTADO</div>
                      )}
                      <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                        {formatear(conIvaSegun(ivaModo, it.precio_unitario_mxn), 'MXN')} c/u
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${COLOR_BORDE}`, borderRadius: 6 }}>
                      <button
                        style={btnQty}
                        aria-label="Restar uno"
                        onClick={() => cambiarCantidad(it.id, -1)}
                      ><Minus size={14} strokeWidth={2} /></button>
                      <InputCantidad
                        valor={Math.max(1, Math.round(Number(it.cantidad) || 1))}
                        onFijar={(n) => fijarCantidad(it.id, n)}
                      />
                      <button
                        style={btnQty}
                        aria-label="Sumar uno"
                        onClick={() => cambiarCantidad(it.id, 1)}
                      ><Plus size={14} strokeWidth={2} /></button>
                    </div>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, textAlign: 'right' }}>
                      {formatear(conIvaSegun(ivaModo, it.subtotal_mxn), 'MXN')}
                    </div>
                    <button
                      onClick={() => quitarItem(it.id)}
                      aria-label="Quitar del carrito"
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer', color: tokens.danger,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4,
                        // Area tactil 44x44 (UI-1.D); el icono Trash2 no cambia de tamano.
                        minWidth: 44, minHeight: 44,
                      }}
                    ><Trash2 size={16} strokeWidth={2} /></button>
                  </div>
                </div>
                );
              })}
            </div>

            <div style={{
              padding: '14px 20px', borderTop: `1px solid ${COLOR_BORDE}`, background: COLOR_FONDO,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 13 }}>{ivaModo === 'con' ? 'Total (con IVA)' : 'Subtotal (sin IVA)'}</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: COLOR_OSCURO }}>
                  {formatear(conIvaSegun(ivaModo, subtotal), 'MXN')}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button
                  style={{
                    ...estiloBotonPrimario, width: '100%', background: tokens.success,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                  onClick={() => setModalCompartir(true)}
                  disabled={items.length === 0}
                ><Link2 size={14} strokeWidth={2} /> Compartir vitrina con cliente</button>
                {agregandoA ? (
                  <button
                    style={{
                      ...estiloBotonPrimario, width: '100%', background: COLOR_OSCURO,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                    onClick={volverDesdeCarrito}
                  ><ArrowLeft size={14} strokeWidth={2} /> Volver a la cotización</button>
                ) : (
                  <button
                    style={{
                      ...estiloBotonPrimario, width: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                    onClick={generarCotizacion}
                    disabled={items.length === 0}
                  ><ArrowRight size={14} strokeWidth={2} /> Generar cotización</button>
                )}
                <button
                  style={{
                    ...estiloBotonSecundario, width: '100%', color: tokens.danger,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                  onClick={() => {
                    if (window.confirm('¿Vaciar carrito completo?')) vaciar();
                  }}
                  disabled={items.length === 0}
                ><Trash2 size={14} strokeWidth={2} /> Vaciar</button>
              </div>
            </div>
          </div>
        </>
      )}
      <style>{`
        @media (max-width: 480px) {
          .carrito-thumb { width: 50px !important; height: 50px !important; }
        }
        @keyframes cartSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes cartSlideRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>

      {modalCompartir && (
        <ModalCompartir
          items={items}
          onCerrar={() => setModalCompartir(false)}
        />
      )}
    </>
  );
}

const btnQty = {
  background: 'transparent', border: 'none', padding: '4px 10px', cursor: 'pointer',
  fontSize: 14, fontWeight: 700, color: COLOR_GRIS,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  // Area tactil 44x44 (UI-1.D); el icono Minus/Plus no cambia de tamano.
  minWidth: 44, minHeight: 44,
};

// Input editable de cantidad (entre los botones - y +). Solo enteros, minimo 1.
// Mantiene texto local mientras se escribe (permite borrar y teclear varios
// digitos); al salir/Enter normaliza a >=1. Se sincroniza desde fuera solo
// cuando NO esta enfocado, para no pisar lo que el usuario teclea.
function InputCantidad({ valor, onFijar }) {
  const [texto, setTexto] = useState(String(valor));
  const enfocadoRef = useRef(false);

  useEffect(() => {
    if (!enfocadoRef.current) setTexto(String(valor));
  }, [valor]);

  const onChange = (e) => {
    const limpio = (e.target.value || '').replace(/[^0-9]/g, '');
    setTexto(limpio);
    const n = parseInt(limpio, 10);
    if (Number.isFinite(n) && n >= 1) onFijar(n);
  };

  const normalizar = () => {
    const n = parseInt(texto, 10);
    const final = Number.isFinite(n) && n >= 1 ? n : 1;
    setTexto(String(final));
    onFijar(final);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      aria-label="Cantidad"
      value={texto}
      onChange={onChange}
      onFocus={(e) => { enfocadoRef.current = true; e.target.select(); }}
      onBlur={() => { enfocadoRef.current = false; normalizar(); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      style={{
        width: 48, textAlign: 'center', border: 'none', background: 'transparent',
        fontSize: 12, fontWeight: 600, color: COLOR_GRIS, fontFamily: PP,
        padding: '4px 2px', outline: 'none', MozAppearance: 'textfield',
      }}
    />
  );
}
